import React, { useState, useRef, useEffect } from 'react';
import { fmtEur, fmtPct } from './utils/fmt';
import { generateKpiContext } from './utils/interpretations';
import { useRules } from '../../hooks/useRules';

/**
 * Wertet eine KPI gegen die Rules aus.
 * Iteriert die conditions des kpiKey und gibt die erste zutreffende zurück.
 *
 * Operatoren: '>', '>=', '<', '<='
 * Gibt { label, badge_bg, badge_text } zurück — Farben aus rules.color_tokens aufgelöst.
 *
 * @param {Object} rules
 * @param {string} kpiKey  - Schlüssel in rules.kpi_rules
 * @param {number|null} value
 * @returns {{ label: string, badge_bg: string, badge_text: string, badge: string } | null}
 */
function applyKpiRule(rules, kpiKey, value) {
  if (value === null || value === undefined || !rules?.kpi_rules?.[kpiKey]) return null;

  const conditions = rules.kpi_rules[kpiKey].conditions;
  const tokens = rules.color_tokens;

  // Badge-Farben je Stufe — aus color_tokens
  // Farben aus rules.color_tokens — immer vorhanden da useRules mit rulesFallback initialisiert
  const badgeColors = {
    gut:        { bg: tokens['cyan-light'],        text: tokens['cyan-dark']           },
    akzeptabel: { bg: tokens['signal-medium-bg'],  text: tokens['signal-medium-text']  },
    kritisch:   { bg: tokens['bg-critical'],       text: tokens['value-red']           },
  };

  for (const cond of conditions) {
    let match = false;
    switch (cond.operator) {
      case '>':  match = value >  cond.threshold; break;
      case '>=': match = value >= cond.threshold; break;
      case '<':  match = value <  cond.threshold; break;
      case '<=': match = value <= cond.threshold; break;
      default:   match = false;
    }

    if (match) {
      const colors = badgeColors[cond.badge] || badgeColors.akzeptabel;
      return {
        label:      cond.text,
        badge:      cond.badge,
        badge_bg:   colors.bg,
        badge_text: colors.text,
      };
    }
  }

  return null;
}

/* ── Calculation Row with expandable details ── */
function CalculationRow({ row }) {
  const [open, setOpen] = useState(false);
  const hasDetails = row.details && row.details.length > 0;
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[14px] font-medium text-[#4A4A52]">{row.label}</span>
        <div className="text-right">
          <div className="flex items-center gap-1">
            <span className="text-[14px] font-semibold text-[#1F1F1F]">{row.value}</span>
            {hasDetails && (
              <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                style={{ background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#8C8FA0',padding:'0 2px',lineHeight:1 }}>
                {open ? '∧' : '∨'}
              </button>
            )}
          </div>
          {row.source && <div className="text-[11px] text-[#9A9AA0]">{row.source}</div>}
        </div>
      </div>
      {hasDetails && open && (
        <div style={{ paddingLeft:16,background:'#F3F3F5',borderRadius:6,padding:'6px 8px 6px 16px',marginTop:2,marginBottom:2 }}>
          {row.details.map((d, j) => (
            <div key={j} className="flex items-baseline justify-between gap-2" style={{marginBottom:2}}>
              <span style={{fontSize:13,color:'#777888'}}>{d.label}</span>
              <div className="flex items-center gap-1">
                <span style={{fontSize:13,fontWeight:600,color:'#1F1F1F'}}>{d.value}</span>
                {d.pct && <span style={{fontSize:12,color:'#8C8FA0'}}>({d.pct})</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ── KPI Drill-down Popup ── */
function KpiPopup({ kpiName, recalcData, baseline, onClose, badgeStyle }) {
  const ref = useRef(null);
  const ctx = generateKpiContext(kpiName, null, { _baseline: baseline, marketData: recalcData?.marketData });

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (!ctx) return null;

  return (
    <div
      ref={ref}
      className="absolute top-full left-1/2 z-50 p-4"
      style={{
        transform: 'translateX(-50%)',
        marginTop: 8,
        width: 320,
        background: '#FFFFFF',
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[16px] font-semibold text-[#1F1F1F]">{ctx.title}</span>
        <button
          onClick={onClose}
          className="text-[#9A9AA0] hover:text-[#1F1F1F] text-[18px] leading-none"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          &times;
        </button>
      </div>

      {/* Was ist das? */}
      <div className="mb-3">
        <div className="text-[12px] font-semibold text-[#9A9AA0] uppercase tracking-[0.5px] mb-1">Was ist das?</div>
        <p className="text-[14px] text-[#4A4A52] leading-[1.5]">{ctx.definition}</p>
      </div>

      {/* Berechnung */}
      {ctx.calculation && (
        <div className="mb-3">
          <div className="text-[12px] font-semibold text-[#9A9AA0] uppercase tracking-[0.5px] mb-1">Berechnung</div>
          <div className="text-[13px] text-[#9A9AA0] font-sans mb-2">{ctx.formulaLabel}</div>
          <div className="space-y-1">
            {ctx.calculation.map((row, i) => <CalculationRow key={i} row={row} />)}
          </div>
        </div>
      )}

      {/* Für dieses Objekt */}
      {ctx.interpretation && (
        <div className="rounded-md p-2" style={{ background: '#E8F7F7' }}>
          <div className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5" style={{ color: '#1DB3B3' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm0 11.5c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75zM9 9.5H7V4.5h2v5z" fill="#1DB3B3"/>
              </svg>
            </span>
            <p className="text-[14px] text-[#4A4A52] leading-[1.5]">{ctx.interpretation}</p>
          </div>
          {ctx.rating && badgeStyle && (
            <span
              className="inline-block mt-2 text-[12px] font-semibold rounded px-2 py-0.5"
              style={{ background: badgeStyle.badge_bg, color: badgeStyle.badge_text }}
            >
              {ctx.rating}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Info Icon ── */
function InfoIcon({ isHovered, isActive }) {
  const color = isActive ? '#1DB3B3' : isHovered ? '#0E7A6B' : '#C8C8D0';
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ color, transition: 'color 150ms ease' }}
    >
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6.3 5.8c0-.97.79-1.5 1.32-1.5s1.32.44 1.32 1.23c0 .71-.62.97-1.32 1.41V8.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="7" cy="9.7" r="0.45" fill="currentColor" />
    </svg>
  );
}

/* ── Single KPI Card ── */
function KpiCard({ label, value, assessment, contextTag, kpiName, recalcData, baseline }) {
  const [showPopup, setShowPopup] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative"
      style={{
        background: isHovered || showPopup ? '#E8F7F7' : '#FFFFFF',
        border: `1px solid ${showPopup ? '#1DB3B3' : isHovered ? '#97D2C0' : '#E0E0DF'}`,
        borderRadius: 12,
        padding: 16,
        cursor: 'pointer',
        transition: 'border-color 150ms ease, background 150ms ease',
        userSelect: 'none',
      }}
      onClick={() => setShowPopup(!showPopup)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Info-Icon oben rechts */}
      <div
        className="absolute"
        style={{ top: 12, right: 12 }}
        onClick={(e) => { e.stopPropagation(); setShowPopup(!showPopup); }}
      >
        <InfoIcon isHovered={isHovered} isActive={showPopup} />
      </div>

      {/* Label */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '18px',
          color: '#94A3B8',
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      {/* Wert */}
      <div
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: '-0.03em',
          color: '#1F1F1F',
          fontVariantNumeric: 'tabular-nums',
          marginBottom: 6,
        }}
      >
        {value}
      </div>

      {/* Badge */}
      {assessment && (
        <span
          style={{
            display: 'inline-block',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 4,
            padding: '2px 6px',
            marginBottom: contextTag ? 4 : 0,
            background: assessment.badge_bg,
            color: assessment.badge_text,
          }}
        >
          {assessment.label}
        </span>
      )}

      {/* ContextTag */}
      {contextTag && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '18px',
            color: '#8C8FA0',
          }}
        >
          {contextTag}
        </div>
      )}

      {/* Popup */}
      {showPopup && (
        <KpiPopup
          kpiName={kpiName}
          recalcData={recalcData}
          baseline={baseline}
          badgeStyle={assessment}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}

/* ── Main Component ── */
export default function KpiRibbon({ baseline, recalcData }) {
  const rules = useRules();

  if (!baseline) return null;

  const cf = baseline.cf;
  const brVal = baseline.bruttoRendite;
  const faktorVal = baseline.faktor;
  const cashflow = cf?.cashflow ?? null;
  const ekVal = baseline.ekRendite;
  const isZeroEk = baseline.eigenkapitalAnteil === 0;

  // Nettomietrendite: (Monatskaltmiete - Bewirtschaftung) * 12 / Gesamtkosten
  const nettoRendite = cf
    ? ((baseline.monatskaltmiete - cf.bewirtschaftung) * 12 / baseline.gesamtkosten) * 100
    : null;

  const safeNettoRendite = nettoRendite != null && isFinite(nettoRendite) ? nettoRendite : null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 12,
      }}
    >
      <KpiCard
        label="Bruttorendite"
        value={brVal != null ? fmtPct(brVal) : '—'}
        assessment={applyKpiRule(rules, 'brutto_rendite', brVal)}
        kpiName="bruttomietrendite"
        recalcData={recalcData}
        baseline={baseline}
      />
      <KpiCard
        label="Nettorendite"
        value={safeNettoRendite != null ? fmtPct(safeNettoRendite) : '—'}
        assessment={applyKpiRule(rules, 'netto_rendite', safeNettoRendite)}
        kpiName="nettomietrendite"
        recalcData={recalcData}
        baseline={baseline}
      />
      <KpiCard
        label="Kaufpreisfaktor"
        value={faktorVal != null ? `${faktorVal.toFixed(1).replace('.', ',')}\u00D7` : '—'}
        assessment={applyKpiRule(rules, 'kaufpreisfaktor', faktorVal)}
        kpiName="kaufpreisfaktor"
        recalcData={recalcData}
        baseline={baseline}
      />
      <KpiCard
        label="Monatlicher Cashflow"
        value={cashflow != null ? fmtEur(cashflow) : '—'}
        assessment={applyKpiRule(rules, 'cashflow', cashflow)}
        kpiName="cashflow"
        recalcData={recalcData}
        baseline={baseline}
      />
      <KpiCard
        label="EK-Rendite"
        value={isZeroEk ? '—' : (ekVal != null ? fmtPct(ekVal) : '—')}
        contextTag={isZeroEk ? 'Vollfinanzierung' : null}
        assessment={isZeroEk ? null : applyKpiRule(rules, 'ek_rendite', ekVal)}
        kpiName="ekRendite"
        recalcData={recalcData}
        baseline={baseline}
      />
    </div>
  );
}
